import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Edit2, Save, Target } from "lucide-react";

export default function Competitors() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const { data } = await api.get("/competitors");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.patch(`/competitors/${form.id}`, form);
      else await api.post("/competitors", form);
      toast.success("Saved");
      setEditing(null); setShowNew(false);
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };
  const remove = async (c) => {
    if (!confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/competitors/${c.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="competitors-page">
      <div className="flex items-end justify-between border-b border-ink-line pb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Intelligence</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Competitors</h1>
          <p className="text-sm text-ink-muted mt-2">Track rival bidders, their strengths, pricing behaviour and win rates.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-competitor-btn" className="bg-brand hover:bg-brand-hover text-white px-4 py-2.5 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors">
          <Plus className="w-4 h-4" /> Add Competitor
        </button>
      </div>

      {items.length === 0 && (
        <div className="card-flat p-16 text-center">
          <Target className="w-10 h-10 text-ink-muted mx-auto mb-3" />
          <div className="text-lg font-display font-semibold">No competitors tracked yet</div>
          <div className="text-sm text-ink-muted mt-1">Start building your competitive intelligence database.</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.map((c) => (
          <div key={c.id} className="card-flat p-5" data-testid={`competitor-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg tracking-tight">{c.name}</div>
                <div className="text-xs text-ink-muted mt-0.5">Tracked competitor</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(c)} className="p-1.5 text-ink-muted hover:text-brand hover:bg-secondary rounded-sm"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(c)} className="p-1.5 text-ink-muted hover:text-warn hover:bg-warn-soft rounded-sm"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-ink-line">
              <div>
                <div className="stat-label">Win Rate</div>
                <div className="font-num text-xl font-bold mt-1 text-good">{c.win_rate || 0}<span className="text-xs text-ink-muted">%</span></div>
              </div>
              <div>
                <div className="stat-label">Typical Markup</div>
                <div className="font-num text-xl font-bold mt-1">{c.typical_markup || 0}<span className="text-xs text-ink-muted">%</span></div>
              </div>
            </div>
            {c.strengths && (<div className="mt-4"><div className="stat-label">Strengths</div><div className="text-sm mt-1">{c.strengths}</div></div>)}
            {c.weaknesses && (<div className="mt-3"><div className="stat-label">Weaknesses</div><div className="text-sm mt-1">{c.weaknesses}</div></div>)}
            {c.notes && (<div className="mt-3"><div className="stat-label">Notes</div><div className="text-sm mt-1 text-ink-muted">{c.notes}</div></div>)}
          </div>
        ))}
      </div>

      {(showNew || editing) && (
        <CompetitorModal initial={editing || {}} onClose={() => { setShowNew(false); setEditing(null); }} onSave={save} />
      )}
    </div>
  );
}

function CompetitorModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    name: initial.name || "", strengths: initial.strengths || "",
    weaknesses: initial.weaknesses || "", typical_markup: initial.typical_markup || 0,
    win_rate: initial.win_rate || 0, notes: initial.notes || "",
  });
  const inputCls = "w-full px-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand";

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(initial.id ? { id: initial.id } : {}),
      ...f,
      typical_markup: parseFloat(f.typical_markup) || 0,
      win_rate: parseFloat(f.win_rate) || 0,
    });
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-xl w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line">
          <div className="font-display font-bold tracking-tight">{initial.id ? "Edit" : "New"} Competitor</div>
          <button onClick={onClose} className="p-1 text-ink-muted hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          <div className="col-span-2"><label className="stat-label block mb-1.5">Name</label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="competitor-name-input" /></div>
          <div><label className="stat-label block mb-1.5">Win Rate (%)</label><input type="number" value={f.win_rate} onChange={(e) => setF({ ...f, win_rate: e.target.value })} className={inputCls} /></div>
          <div><label className="stat-label block mb-1.5">Typical Markup (%)</label><input type="number" value={f.typical_markup} onChange={(e) => setF({ ...f, typical_markup: e.target.value })} className={inputCls} /></div>
          <div className="col-span-2"><label className="stat-label block mb-1.5">Strengths</label><textarea rows={2} value={f.strengths} onChange={(e) => setF({ ...f, strengths: e.target.value })} className={inputCls} /></div>
          <div className="col-span-2"><label className="stat-label block mb-1.5">Weaknesses</label><textarea rows={2} value={f.weaknesses} onChange={(e) => setF({ ...f, weaknesses: e.target.value })} className={inputCls} /></div>
          <div className="col-span-2"><label className="stat-label block mb-1.5">Notes</label><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} /></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary">Cancel</button>
            <button type="submit" data-testid="competitor-save-btn" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-1.5"><Save className="w-4 h-4" /> Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
